import { Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';

import { environment } from '#environments/environment';
import { LineOfBusiness } from '#services/cve/VettingIteration';
import { SupportedBuCode, supportedBuCodes } from '#services/user/user.service';

const defaultFeatureToggles = {
  lob: environment.featureToggles.lineOfBusiness,
  translations: environment.featureToggles.translations,
  massUpload: environment.featureToggles.massUpload,
  gradeAutoRefresh: environment.featureToggles.gradeAutoRefresh,
  supportRequest: (buCode: SupportedBuCode, lob: LineOfBusiness) => {
    const toggle = environment.featureToggles.supportRequest[buCode] as boolean | LineOfBusiness[];
    return typeof toggle === 'boolean' ? toggle : toggle.includes(lob);
  },
  esgScoringTool: (buCode: SupportedBuCode) => environment.featureToggles.esgScoringTool[buCode],
} as const;

type SimpleFeatureToggleKey = 'lob' | 'massUpload' | 'translations' | 'gradeAutoRefresh';
type BuCodeAndLineOfBusinessSpecificFeatureToggleKey = 'supportRequest' | 'esgScoringTool';
export type FeatureToggleKey =
  | SimpleFeatureToggleKey
  | `${BuCodeAndLineOfBusinessSpecificFeatureToggleKey}_${SupportedBuCode}_${LineOfBusiness}`;

const supportedLinesOfBusiness = Object.keys(LineOfBusiness) as LineOfBusiness[];

type ComputedSpecificFeatureToggles = {
  [K in `${BuCodeAndLineOfBusinessSpecificFeatureToggleKey}_${SupportedBuCode}_${LineOfBusiness}`]: WritableSignal<boolean>;
};

@Injectable({
  providedIn: 'root',
})
export default class FeatureToggleService {
  private readonly featureTogglesKeys = Object.keys(defaultFeatureToggles) as (keyof typeof defaultFeatureToggles)[];
  private readonly featureToggleSignals = this.featureTogglesKeys.reduce(
    (prev, ftKey) => {
      if (ftKey === 'lob' || ftKey === 'massUpload' || ftKey === 'translations' || ftKey === 'gradeAutoRefresh') {
        return {
          ...prev,
          [ftKey]: signal(defaultFeatureToggles[ftKey]),
        };
      }
      const toggleForEachBuCode = supportedBuCodes.reduce(
        (acc, buCode) => ({
          ...acc,
          ...supportedLinesOfBusiness.reduce(
            (lobAcc, lob) => ({
              ...lobAcc,
              [`${ftKey}_${buCode}_${lob}`]: signal(defaultFeatureToggles[ftKey](buCode, lob)),
            }),
            {} as ComputedSpecificFeatureToggles,
          ),
        }),
        {} as ComputedSpecificFeatureToggles,
      );
      return {
        ...prev,
        ...toggleForEachBuCode,
      };
    },
    {} as {
      [K in FeatureToggleKey]: WritableSignal<boolean>;
    },
  );

  loadFeatureToggles(route: ActivatedRouteSnapshot) {
    Object.keys(route.queryParams).forEach((queryParamKey) => {
      const featureToggle = queryParamKey.slice(1);
      if (!this.isValidFeatureToggle(featureToggle)) return;
      this.parseAndSetFeatureToggle(featureToggle, route.queryParams[queryParamKey]);
    });
  }

  getFeatureToggleSignal(key: FeatureToggleKey): Signal<boolean> {
    return (this.isValidFeatureToggle(key) && this.featureToggleSignals[key]) || signal(false);
  }

  private isValidFeatureToggle(featureToggle: string): featureToggle is FeatureToggleKey {
    return this.featureToggleSignals[featureToggle as FeatureToggleKey] !== undefined;
  }

  private parseAndSetFeatureToggle(feature: FeatureToggleKey, inputToggle: string) {
    switch (inputToggle) {
      case 'true':
        this.featureToggleSignals[feature]?.set(true);
        break;
      case 'false':
        this.featureToggleSignals[feature]?.set(false);
        break;
      default:
        console.warn(`"${inputToggle}" is an incorrect value for toggle: _${feature}. Must be "true" or "false".`);
    }
  }
}
